import { onUnmounted, ref, shallowRef } from 'vue';
import { throttle as _throttle } from 'lodash';
import log from '../../services/Logger';
import { DataItemSlice, Range } from "./slice";
/**
 * Loading and caching of the items: keeps a slice (window) of the result set,
 * loads pages on demand for the visible range, drops responses of superseded
 * requests.
 */
export function useDataTableItems(options) {
    const slice = shallowRef(DataItemSlice.empty());
    const totalElements = ref(0);
    const moreElements = ref(false);
    const loading = ref(false);
    const tooManyResults = ref(false);
    const pageSize = ref(20);
    // The data height is not a computed on purpose: it is only updated after
    //   a load completes (like the item slice), so that resetting the slice
    //   does not collapse the scroll container and cause a second load.
    const dataHeight = ref(0);
    const dataPaddingTop = ref(0);
    let requestNum = 0;
    function loadItems(offset) {
        tooManyResults.value = false;
        loading.value = true;
        const pageNumber = Math.floor(offset / pageSize.value);
        if ((pageNumber * pageSize.value) + pageSize.value >= options.maxItems.value) {
            log('getting above maxItems: not loading any more', offset, options.maxItems.value);
            tooManyResults.value = true;
            loading.value = false;
            return;
        }
        log(`loadItems - offset=${offset}, pageNumber=${pageNumber}`);
        const sortColumn = options.sortColumn.value;
        const attributes = options.selectedColumnNames.value;
        const thisRequest = ++requestNum;
        options.source.value
            .get(options.prepareSearchRequest(options.searchRequest.value), {
            number: pageNumber,
            size: pageSize.value,
            sort: sortColumn && sortColumn.name,
            order: sortColumn && sortColumn.sortDirection
        }, attributes)
            .then(page => {
            if (thisRequest != requestNum) {
                log('throwing away results from old request #' + thisRequest);
                return;
            }
            log('received items', page);
            options.onPageLoaded(page);
            totalElements.value = page.totalElements;
            moreElements.value = page.number < (page.totalPages - 1);
            const pageOffset = page.number * page.size;
            mergeItems(new DataItemSlice(new Range(pageOffset, pageOffset + page.size), page.content));
        })
            .catch(error => {
            options.onLoadError(error);
        })
            .finally(() => {
            refreshDataHeight();
            loading.value = false;
        });
    }
    function mergeItems(other) {
        const current = slice.value;
        if (current.range.intersectsOrTouches(other.range)) {
            log(`adding to current slice ${current.range.from}..${current.range.to} + ${other.range.from}..${other.range.to}`);
            slice.value = current.combine(other);
        }
        else {
            log(`replacing current slice ${current.range.from}..${current.range.to} + ${other.range.from}..${other.range.to}`);
            slice.value = other;
        }
    }
    function refreshDataHeight() {
        const itemsCount = Math.min(options.maxItems.value, totalElements.value);
        dataHeight.value = itemsCount * options.rowHeight.value;
        dataPaddingTop.value = slice.value.range.from * options.rowHeight.value;
    }
    /**
     * Discards the loaded items and loads the first page.
     */
    function resetSlice() {
        slice.value = DataItemSlice.empty();
        totalElements.value = 0;
        loadItems(0);
    }
    /**
     * Reloads the page at the beginning of the current slice.
     */
    function reloadCurrent() {
        loadItems(slice.value.range.from);
    }
    /**
     * Makes sure the items for the given (visible) range are loaded or being
     * loaded.
     */
    function ensureRangeUnthrottled(from, to) {
        const range = new Range(from, to);
        const current = slice.value;
        if (current.range.contains(range)) {
            return;
        }
        log(`refreshing items for range=[${range.from}..${range.to}] (current=[${current.range.from}..${current.range.to}], pageSize=${pageSize.value})`);
        if (current.range.intersectsOrTouches(range)) {
            // append?
            if (range.to > current.range.to) {
                log('need more items to append', range, current);
                loadItems(current.range.to);
            }
            // prepend?
            if (range.from < current.range.from) {
                log('need more items to prepend', range, current);
                loadItems(current.range.from - pageSize.value);
            }
            return;
        }
        log('need more items to replace current slice');
        loadItems(range.from);
    }
    const ensureRange = _throttle(ensureRangeUnthrottled, 200);
    onUnmounted(() => ensureRange.cancel());
    return {
        slice,
        totalElements,
        moreElements,
        loading,
        tooManyResults,
        pageSize,
        dataHeight,
        dataPaddingTop,
        loadItems,
        resetSlice,
        reloadCurrent,
        ensureRange,
    };
}
